import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useAuthStore from '../../store/authStore';
import useProjectStore from '../../store/projectStore';
import useUIStore from '../../store/uiStore';
import * as expenseService from '../../services/expenseService';
import ExpenseTabs from '../../components/ExpenseTabs';
import { downloadBlob } from '../../utils/downloadFile';
import ScrollX from '../../components/ScrollX';

const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

const LOGGER_ROLES = ['Accounts_Manager', 'System_Admin', 'CEO'];

// A vendor advance/bill must link to exactly one of project / factory bill /
// fixed asset (chk_vendor_advances_one_link / chk_vendor_bills_one_link in
// 014_expense_management.sql) - the form below enforces "pick a link type,
// then pick the specific target" so that's structurally impossible to violate.
const LINK_TYPES = [
  { value: 'project', label: 'Project' },
  { value: 'factory_bill', label: 'Factory Bill' },
  { value: 'fixed_asset', label: 'Fixed Asset' },
];

const initialVendorForm = { name: '', contact_person: '', phone: '', email: '', gstin: '', category: '', address: '' };
const initialAdvanceForm = { vendor_id: '', link_type: 'project', link_id: '', amount: '', given_date: '', notes: '' };
const initialBillForm = { vendor_id: '', advance_id: '', link_type: 'project', link_id: '', amount: '', description: '', bill_date: '', po_number: '' };
const initialFixedAssetForm = { asset_name: '', asset_type: '', project_id: '', purchase_date: '', purchase_value: '', vendor_id: '', location: '' };

const VendorPaymentsPage = () => {
  const { user } = useAuthStore();
  const { projects, fetchProjects } = useProjectStore();
  const showToast = useUIStore((s) => s.showToast);
  const location = useLocation();
  const navigate = useNavigate();

  // Drill-down target from the dashboard's "Expenses by Vendor" chart
  // (?vendor_id=..) - seeds the real filter bar below rather than a
  // separate client-side filter, so it scales the same way as picking a
  // vendor by hand.
  const urlVendorId = useMemo(() => {
    const v = new URLSearchParams(location.search).get('vendor_id');
    return v ? Number(v) : null;
  }, [location.search]);

  const initialFilters = { vendor_id: '', project_id: '', date_from: '', date_to: '' };
  const [filters, setFilters] = useState(() => (urlVendorId ? { ...initialFilters, vendor_id: String(urlVendorId) } : initialFilters));
  const clearFilters = () => { setFilters(initialFilters); navigate('/expenses/vendor'); };

  const [vendors, setVendors] = useState([]);
  const [fixedAssets, setFixedAssets] = useState([]);
  const [factoryBills, setFactoryBills] = useState([]);
  const [advances, setAdvances] = useState([]);
  const [bills, setBills] = useState([]);
  const [settlements, setSettlements] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [showVendorForm, setShowVendorForm] = useState(false);
  const [showAdvanceForm, setShowAdvanceForm] = useState(false);
  const [showBillForm, setShowBillForm] = useState(false);
  const [showFixedAssetForm, setShowFixedAssetForm] = useState(false);
  const [vendorForm, setVendorForm] = useState(initialVendorForm);
  const [advanceForm, setAdvanceForm] = useState(initialAdvanceForm);
  const [billForm, setBillForm] = useState(initialBillForm);
  const [isImporting, setIsImporting] = useState(false);
  const [fixedAssetForm, setFixedAssetForm] = useState(initialFixedAssetForm);

  // 2026-08-19 user instruction: "everyone can log in all money, HOD/
  // accounts/admin/CEO approve it" - logging (create/edit/delete a vendor,
  // fixed asset, advance, or bill) is open to anyone logged in. Mirrors
  // expense_routes.py's create/update/delete_* endpoints for these now
  // using get_current_employee instead of require_role. Two exceptions
  // stay restricted since they're not raw "logging": deactivating a
  // vendor (master-data lifecycle) and marking a settlement settled
  // (closing the books) - both still require LOGGER_ROLES server-side.
  const canLog = !!user;
  const canManageVendors = user && LOGGER_ROLES.includes(user.role);

  const loadAll = useCallback(async () => {
    setIsLoading(true);
    try {
      const advanceBillParams = {};
      if (filters.vendor_id) advanceBillParams.vendor_id = filters.vendor_id;
      if (filters.project_id) advanceBillParams.project_id = filters.project_id;
      const billParams = { ...advanceBillParams };
      if (filters.date_from) billParams.date_from = filters.date_from;
      if (filters.date_to) billParams.date_to = filters.date_to;

      const [vendRes, assetRes, fbillRes, advRes, billRes, setRes] = await Promise.all([
        expenseService.getVendors(),
        expenseService.getFixedAssets(),
        expenseService.getFactoryBills(),
        expenseService.getVendorAdvances(advanceBillParams),
        expenseService.getVendorBills(billParams),
        expenseService.getVendorSettlements(),
      ]);
      setVendors(vendRes.data.vendors || []);
      setFixedAssets(assetRes.data.fixed_assets || []);
      setFactoryBills(fbillRes.data.bills || []);
      setAdvances(advRes.data.advances || []);
      setBills(billRes.data.bills || []);
      setSettlements(setRes.data.settlements || []);
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to load vendor payments', 'error');
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => {
    fetchProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const linkOptions = (linkType) => {
    if (linkType === 'project') return projects.map((p) => ({ id: p.id, label: p.name }));
    if (linkType === 'factory_bill') return factoryBills.map((b) => ({ id: b.id, label: `${b.type} - ${money(b.amount)} (${b.date_received})` }));
    return fixedAssets.map((a) => ({ id: a.id, label: a.asset_name }));
  };

  const linkPayload = (linkType, linkId) => ({
    project_id: linkType === 'project' ? Number(linkId) : null,
    factory_bill_id: linkType === 'factory_bill' ? Number(linkId) : null,
    fixed_asset_id: linkType === 'fixed_asset' ? Number(linkId) : null,
  });

  const vendorName = (id) => vendors.find((v) => v.id === id)?.name || '—';

  const linkLabel = (row) => {
    if (row.project_id) return projects.find((p) => p.id === row.project_id)?.name || `Project #${row.project_id}`;
    if (row.factory_bill_id) return `Factory Bill #${row.factory_bill_id}`;
    if (row.fixed_asset_id) return fixedAssets.find((a) => a.id === row.fixed_asset_id)?.asset_name || `Asset #${row.fixed_asset_id}`;
    return '—';
  };

  const handleVendorSubmit = async (e) => {
    e.preventDefault();
    if (!vendorForm.name) {
      showToast('Vendor name is required', 'error');
      return;
    }
    try {
      await expenseService.createVendor(vendorForm);
      showToast('Vendor added', 'success');
      setVendorForm(initialVendorForm);
      setShowVendorForm(false);
      loadAll();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to add vendor', 'error');
    }
  };

  const handleAdvanceSubmit = async (e) => {
    e.preventDefault();
    if (!advanceForm.vendor_id || !advanceForm.link_id || !advanceForm.amount) {
      showToast('Vendor, link target, and amount are required', 'error');
      return;
    }
    try {
      await expenseService.createVendorAdvance({
        vendor_id: Number(advanceForm.vendor_id),
        ...linkPayload(advanceForm.link_type, advanceForm.link_id),
        amount: Number(advanceForm.amount),
        given_date: advanceForm.given_date || null,
        notes: advanceForm.notes || null,
      });
      showToast('Advance recorded', 'success');
      setAdvanceForm(initialAdvanceForm);
      setShowAdvanceForm(false);
      loadAll();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to record advance', 'error');
    }
  };

  const handleBillSubmit = async (e) => {
    e.preventDefault();
    if (!billForm.vendor_id || !billForm.link_id || !billForm.amount || !billForm.bill_date) {
      showToast('Vendor, link target, amount, and bill date are required', 'error');
      return;
    }
    try {
      await expenseService.createVendorBill({
        vendor_id: Number(billForm.vendor_id),
        advance_id: billForm.advance_id ? Number(billForm.advance_id) : null,
        ...linkPayload(billForm.link_type, billForm.link_id),
        amount: Number(billForm.amount),
        description: billForm.description || null,
        bill_date: billForm.bill_date,
        po_number: billForm.po_number || null,
      });
      showToast('Bill logged', 'success');
      setBillForm(initialBillForm);
      setShowBillForm(false);
      loadAll();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to log bill', 'error');
    }
  };

  // Excel template with real dropdowns (vendor/link_type/link_name, all
  // sourced live from the DB) - see SiteExpensesPage.js's matching comment
  // for why this isn't CSV. factory_bill links still aren't offered
  // (no unique human-readable name to put in a dropdown) - use the form
  // for those.
  const handleDownloadVendorBillTemplate = async () => {
    try {
      const res = await expenseService.getVendorBillsImportTemplate();
      downloadBlob(res.data, 'vendor_bills_template.xlsx');
    } catch (error) {
      showToast('Failed to download template', 'error');
    }
  };

  const handleImportVendorBillsCsv = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setIsImporting(true);
    try {
      const res = await expenseService.importVendorBills(file);
      const count = res.data.bills.length;
      showToast(`${count} bill${count !== 1 ? 's' : ''} imported`, 'success');
      loadAll();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to import file', 'error');
    } finally {
      setIsImporting(false);
    }
  };

  const handleMarkSettled = async (vendorId, vendorName) => {
    if (!window.confirm(`Mark all active advances for ${vendorName || 'this vendor'} as settled/paid?`)) return;
    try {
      await expenseService.markVendorSettlementSettled(vendorId);
      showToast('Marked settled', 'success');
      loadAll();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to mark settled', 'error');
    }
  };

  const handleFixedAssetSubmit = async (e) => {
    e.preventDefault();
    if (!fixedAssetForm.asset_name) {
      showToast('Asset name is required', 'error');
      return;
    }
    try {
      await expenseService.createFixedAsset({
        asset_name: fixedAssetForm.asset_name,
        asset_type: fixedAssetForm.asset_type || null,
        project_id: fixedAssetForm.project_id ? Number(fixedAssetForm.project_id) : null,
        purchase_date: fixedAssetForm.purchase_date || null,
        purchase_value: fixedAssetForm.purchase_value ? Number(fixedAssetForm.purchase_value) : null,
        vendor_id: fixedAssetForm.vendor_id ? Number(fixedAssetForm.vendor_id) : null,
        location: fixedAssetForm.location || null,
      });
      showToast('Fixed asset added', 'success');
      setFixedAssetForm(initialFixedAssetForm);
      setShowFixedAssetForm(false);
      loadAll();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to add fixed asset', 'error');
    }
  };

  const handleEditVendor = async (vendor) => {
    const name = window.prompt('Vendor name:', vendor.name);
    if (name === null) return;
    if (!name.trim()) {
      showToast('Vendor name is required', 'error');
      return;
    }
    try {
      await expenseService.updateVendor(vendor.id, { name });
      showToast('Vendor updated', 'success');
      loadAll();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to update vendor', 'error');
    }
  };

  const handleDeactivateVendor = async (vendor) => {
    if (!window.confirm(`Deactivate ${vendor.name}? It stays on past records but drops out of the picker for new advances/bills.`)) return;
    try {
      await expenseService.deactivateVendor(vendor.id);
      showToast('Vendor deactivated', 'success');
      loadAll();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to deactivate vendor', 'error');
    }
  };

  const handleEditVendorAdvance = async (advance) => {
    const amount = window.prompt('New amount (₹):', advance.amount);
    if (amount === null) return;
    if (!amount || isNaN(Number(amount)) || Number(amount) <= 0) {
      showToast('Enter a valid amount', 'error');
      return;
    }
    try {
      await expenseService.updateVendorAdvance(advance.id, { amount: Number(amount) });
      showToast('Advance updated', 'success');
      loadAll();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to update advance', 'error');
    }
  };

  const handleDeleteVendorAdvance = async (advance) => {
    if (!window.confirm('Delete this advance? This cannot be undone.')) return;
    try {
      await expenseService.deleteVendorAdvance(advance.id);
      showToast('Advance deleted', 'success');
      loadAll();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to delete advance', 'error');
    }
  };

  const handleEditBill = async (bill) => {
    const amount = window.prompt('New amount (₹):', bill.amount);
    if (amount === null) return;
    if (!amount || isNaN(Number(amount)) || Number(amount) <= 0) {
      showToast('Enter a valid amount', 'error');
      return;
    }
    try {
      await expenseService.updateVendorBill(bill.id, { amount: Number(amount) });
      showToast('Bill updated', 'success');
      loadAll();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to update bill', 'error');
    }
  };

  const handleDeleteBill = async (bill) => {
    if (!window.confirm('Delete this bill? This cannot be undone.')) return;
    try {
      await expenseService.deleteVendorBill(bill.id);
      showToast('Bill deleted', 'success');
      loadAll();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to delete bill', 'error');
    }
  };

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-6xl mx-auto">
        <ExpenseTabs />
        <h1 className="text-2xl font-bold text-white mb-6">Vendor Payments</h1>

        <div className="glass-card p-6 mb-6">
          <h2 className="font-semibold text-gray-900 mb-3">Settlements by vendor</h2>
          {settlements.length === 0 ? (
            <p className="text-sm text-gray-400">No advances or bills recorded yet.</p>
          ) : (
            <ScrollX>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-gray-100">
                    <th className="px-3 py-2 font-medium">Vendor</th>
                    <th className="px-3 py-2 font-medium">Advanced</th>
                    <th className="px-3 py-2 font-medium">Billed</th>
                    <th className="px-3 py-2 font-medium">To Pay</th>
                    <th className="px-3 py-2 font-medium">To Receive</th>
                    <th className="px-3 py-2 font-medium">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {settlements.map((s) => (
                    <tr key={s.vendor_id} className="border-b border-gray-50 last:border-0">
                      <td className="px-3 py-2">{s.vendor_name || s.vendor_id}</td>
                      <td className="px-3 py-2">{money(s.total_advanced)}</td>
                      <td className="px-3 py-2">{money(s.total_billed)}</td>
                      <td className="px-3 py-2 font-medium">{money(s.amount_to_pay)}</td>
                      <td className="px-3 py-2 font-medium">{money(s.amount_to_receive)}</td>
                      <td className="px-3 py-2">
                        {(s.amount_to_pay > 0 || s.amount_to_receive > 0) && canManageVendors && (
                          <button onClick={() => handleMarkSettled(s.vendor_id, s.vendor_name)} className="text-green-700 hover:underline text-xs font-semibold">Mark Settled</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ScrollX>
          )}
        </div>

        {/* Advances Given */}
        <div className="glass-card mb-6">
          <div className="px-5 py-4 border-b border-gray-200">
            <h2 className="font-semibold text-gray-900">Advances Given ({advances.length})</h2>
          </div>
          {advances.length === 0 ? (
            <p className="text-gray-500 text-sm p-5">No advances given yet.</p>
          ) : (
            <ScrollX>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-100">
                  <th className="px-4 py-2 font-medium">Vendor</th>
                  <th className="px-4 py-2 font-medium">Linked to</th>
                  <th className="px-4 py-2 font-medium">Amount</th>
                  <th className="px-4 py-2 font-medium">Given Date</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {advances.map((a) => (
                  <tr key={a.id} className="border-b border-gray-50 last:border-0">
                    <td className="px-4 py-3">{vendorName(a.vendor_id)}</td>
                    <td className="px-4 py-3 text-gray-500">{linkLabel(a)}</td>
                    <td className="px-4 py-3 font-medium">{money(a.amount)}</td>
                    <td className="px-4 py-3">{a.given_date}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-1 rounded font-semibold ${a.status === 'Active' ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'}`}>
                        {a.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 space-x-2 whitespace-nowrap">
                      {a.status === 'Active' && canLog && (
                        <>
                          <button onClick={() => handleEditVendorAdvance(a)} className="text-gray-600 hover:underline text-xs font-semibold">Edit</button>
                          <button onClick={() => handleDeleteVendorAdvance(a)} className="text-red-500 hover:underline text-xs font-semibold">Delete</button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </ScrollX>
          )}
        </div>

        {/* Vendors */}
        <div className="glass-card mb-6">
          <div className="px-5 py-4 border-b border-gray-200">
            <h2 className="font-semibold text-gray-900">Vendors ({vendors.length})</h2>
          </div>
          {vendors.length === 0 ? (
            <p className="text-gray-500 text-sm p-5">No vendors added yet.</p>
          ) : (
            <ScrollX>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-100">
                  <th className="px-4 py-2 font-medium">Name</th>
                  <th className="px-4 py-2 font-medium">Category</th>
                  <th className="px-4 py-2 font-medium">Contact</th>
                  <th className="px-4 py-2 font-medium">GSTIN</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {vendors.map((v) => (
                  <tr key={v.id} className="border-b border-gray-50 last:border-0">
                    <td className="px-4 py-3 font-medium">{v.name}</td>
                    <td className="px-4 py-3 text-gray-500">{v.category || '—'}</td>
                    <td className="px-4 py-3 text-gray-500">{v.contact_person || v.phone || v.email || '—'}</td>
                    <td className="px-4 py-3 text-gray-500">{v.gstin || '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-1 rounded font-semibold ${v.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-600'}`}>
                        {v.is_active ? 'Active' : 'Deactivated'}
                      </span>
                    </td>
                    <td className="px-4 py-3 space-x-2 whitespace-nowrap">
                      {canLog && (
                        <button onClick={() => handleEditVendor(v)} className="text-gray-600 hover:underline text-xs font-semibold">Edit</button>
                      )}
                      {canManageVendors && v.is_active && (
                        <button onClick={() => handleDeactivateVendor(v)} className="text-red-500 hover:underline text-xs font-semibold">Deactivate</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </ScrollX>
          )}
        </div>

        {/* Fixed Assets */}
        <div className="glass-card mb-6">
          <div className="px-5 py-4 border-b border-gray-200">
            <h2 className="font-semibold text-gray-900">Fixed Assets ({fixedAssets.length})</h2>
          </div>
          {fixedAssets.length === 0 ? (
            <p className="text-gray-500 text-sm p-5">No fixed assets added yet.</p>
          ) : (
            <ScrollX>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-100">
                  <th className="px-4 py-2 font-medium">Asset</th>
                  <th className="px-4 py-2 font-medium">Type</th>
                  <th className="px-4 py-2 font-medium">Project</th>
                  <th className="px-4 py-2 font-medium">Purchase Value</th>
                  <th className="px-4 py-2 font-medium">Location</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {fixedAssets.map((a) => (
                  <tr key={a.id} className="border-b border-gray-50 last:border-0">
                    <td className="px-4 py-3 font-medium">{a.asset_name}</td>
                    <td className="px-4 py-3 text-gray-500">{a.asset_type || '—'}</td>
                    <td className="px-4 py-3 text-gray-500">{a.project_id ? (projects.find((p) => p.id === a.project_id)?.name || `#${a.project_id}`) : '—'}</td>
                    <td className="px-4 py-3">{a.purchase_value ? money(a.purchase_value) : '—'}</td>
                    <td className="px-4 py-3 text-gray-500">{a.location || '—'}</td>
                    <td className="px-4 py-3 text-gray-500 capitalize">{a.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </ScrollX>
          )}
        </div>

        {canLog && (
          <div className="glass-card p-6 mb-6">
            <button onClick={() => setShowFixedAssetForm(!showFixedAssetForm)} className="text-sm font-semibold text-komal-blue hover:underline">
              {showFixedAssetForm ? 'Hide' : '+ Add a fixed asset'}
            </button>
            {showFixedAssetForm && (
              <form onSubmit={handleFixedAssetSubmit} className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-gray-100">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Asset name *</label>
                  <input required value={fixedAssetForm.asset_name} onChange={(e) => setFixedAssetForm({ ...fixedAssetForm, asset_name: e.target.value })} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <input placeholder="Machinery / Vehicle / IT Equipment / ..." value={fixedAssetForm.asset_type} onChange={(e) => setFixedAssetForm({ ...fixedAssetForm, asset_type: e.target.value })} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Project (optional)</label>
                  <select value={fixedAssetForm.project_id} onChange={(e) => setFixedAssetForm({ ...fixedAssetForm, project_id: e.target.value })} className="w-full border border-gray-300 rounded px-3 py-2 text-sm">
                    <option value="">General / shared</option>
                    {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Vendor (optional)</label>
                  <select value={fixedAssetForm.vendor_id} onChange={(e) => setFixedAssetForm({ ...fixedAssetForm, vendor_id: e.target.value })} className="w-full border border-gray-300 rounded px-3 py-2 text-sm">
                    <option value="">Unknown</option>
                    {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Purchase date</label>
                  <input type="date" value={fixedAssetForm.purchase_date} onChange={(e) => setFixedAssetForm({ ...fixedAssetForm, purchase_date: e.target.value })} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Purchase value (₹)</label>
                  <input type="number" value={fixedAssetForm.purchase_value} onChange={(e) => setFixedAssetForm({ ...fixedAssetForm, purchase_value: e.target.value })} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                  <input value={fixedAssetForm.location} onChange={(e) => setFixedAssetForm({ ...fixedAssetForm, location: e.target.value })} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                </div>
                <div className="col-span-2">
                  <button type="submit" className="px-4 py-2 text-sm rounded bg-komal-blue hover:bg-komal-blue-dark text-white font-semibold">Add Fixed Asset</button>
                </div>
              </form>
            )}
          </div>
        )}

        {canLog && (
          <div className="glass-card p-6 mb-6">
            <button onClick={() => setShowVendorForm(!showVendorForm)} className="text-sm font-semibold text-komal-blue hover:underline">
              {showVendorForm ? 'Hide' : '+ Add a vendor'}
            </button>
            {showVendorForm && (
              <form onSubmit={handleVendorSubmit} className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-gray-100">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input required value={vendorForm.name} onChange={(e) => setVendorForm({ ...vendorForm, name: e.target.value })} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <input placeholder="Contractor / Supplier / ..." value={vendorForm.category} onChange={(e) => setVendorForm({ ...vendorForm, category: e.target.value })} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contact person</label>
                  <input value={vendorForm.contact_person} onChange={(e) => setVendorForm({ ...vendorForm, contact_person: e.target.value })} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input value={vendorForm.phone} onChange={(e) => setVendorForm({ ...vendorForm, phone: e.target.value })} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input value={vendorForm.email} onChange={(e) => setVendorForm({ ...vendorForm, email: e.target.value })} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">GSTIN</label>
                  <input value={vendorForm.gstin} onChange={(e) => setVendorForm({ ...vendorForm, gstin: e.target.value })} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                </div>
                <div className="col-span-2">
                  <button type="submit" className="px-4 py-2 text-sm rounded bg-komal-blue hover:bg-komal-blue-dark text-white font-semibold">Add Vendor</button>
                </div>
              </form>
            )}
          </div>
        )}

        {canLog && (
          <div className="glass-card p-6 mb-6">
            <button onClick={() => setShowAdvanceForm(!showAdvanceForm)} className="text-sm font-semibold text-komal-blue hover:underline">
              {showAdvanceForm ? 'Hide' : '+ Give an advance'}
            </button>
            {showAdvanceForm && (
              <form onSubmit={handleAdvanceSubmit} className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-gray-100">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Vendor *</label>
                  <select required value={advanceForm.vendor_id} onChange={(e) => setAdvanceForm({ ...advanceForm, vendor_id: e.target.value })} className="w-full border border-gray-300 rounded px-3 py-2 text-sm">
                    <option value="">Select vendor</option>
                    {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount (₹) *</label>
                  <input required type="number" value={advanceForm.amount} onChange={(e) => setAdvanceForm({ ...advanceForm, amount: e.target.value })} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Link to *</label>
                  <select value={advanceForm.link_type} onChange={(e) => setAdvanceForm({ ...advanceForm, link_type: e.target.value, link_id: '' })} className="w-full border border-gray-300 rounded px-3 py-2 text-sm">
                    {LINK_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">&nbsp;</label>
                  <select required value={advanceForm.link_id} onChange={(e) => setAdvanceForm({ ...advanceForm, link_id: e.target.value })} className="w-full border border-gray-300 rounded px-3 py-2 text-sm">
                    <option value="">Select {LINK_TYPES.find((t) => t.value === advanceForm.link_type)?.label.toLowerCase()}</option>
                    {linkOptions(advanceForm.link_type).map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                  </select>
                </div>
                <div className="col-span-2">
                  <button type="submit" className="px-4 py-2 text-sm rounded bg-komal-blue hover:bg-komal-blue-dark text-white font-semibold">Give Advance</button>
                </div>
              </form>
            )}
          </div>
        )}

        {canLog && (
          <div className="glass-card p-6 mb-6">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <button onClick={() => setShowBillForm(!showBillForm)} className="text-sm font-semibold text-komal-blue hover:underline">
                {showBillForm ? 'Hide' : '+ Log a bill'}
              </button>
              <div className="flex items-center gap-2">
                <button onClick={handleDownloadVendorBillTemplate} className="text-xs px-3 py-1.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-50">
                  Download Excel Template
                </button>
                <label className="text-xs px-3 py-1.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 cursor-pointer">
                  {isImporting ? 'Importing...' : 'Upload Excel'}
                  <input type="file" accept=".xlsx,.xlsm" onChange={handleImportVendorBillsCsv} disabled={isImporting} className="hidden" />
                </label>
              </div>
            </div>
            {showBillForm && (
              <form onSubmit={handleBillSubmit} className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-gray-100">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Vendor *</label>
                  <select required value={billForm.vendor_id} onChange={(e) => setBillForm({ ...billForm, vendor_id: e.target.value, advance_id: '' })} className="w-full border border-gray-300 rounded px-3 py-2 text-sm">
                    <option value="">Select vendor</option>
                    {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Settle against advance</label>
                  <select value={billForm.advance_id} onChange={(e) => setBillForm({ ...billForm, advance_id: e.target.value })} className="w-full border border-gray-300 rounded px-3 py-2 text-sm">
                    <option value="">None</option>
                    {advances.filter((a) => String(a.vendor_id) === String(billForm.vendor_id)).map((a) => (
                      <option key={a.id} value={a.id}>{money(a.amount)} on {a.given_date}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount (₹) *</label>
                  <input required type="number" value={billForm.amount} onChange={(e) => setBillForm({ ...billForm, amount: e.target.value })} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bill date *</label>
                  <input required type="date" value={billForm.bill_date} onChange={(e) => setBillForm({ ...billForm, bill_date: e.target.value })} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Link to *</label>
                  <select value={billForm.link_type} onChange={(e) => setBillForm({ ...billForm, link_type: e.target.value, link_id: '' })} className="w-full border border-gray-300 rounded px-3 py-2 text-sm">
                    {LINK_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">&nbsp;</label>
                  <select required value={billForm.link_id} onChange={(e) => setBillForm({ ...billForm, link_id: e.target.value })} className="w-full border border-gray-300 rounded px-3 py-2 text-sm">
                    <option value="">Select {LINK_TYPES.find((t) => t.value === billForm.link_type)?.label.toLowerCase()}</option>
                    {linkOptions(billForm.link_type).map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">PO number</label>
                  <input value={billForm.po_number} onChange={(e) => setBillForm({ ...billForm, po_number: e.target.value })} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <input value={billForm.description} onChange={(e) => setBillForm({ ...billForm, description: e.target.value })} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                </div>
                <div className="col-span-2">
                  <button type="submit" className="px-4 py-2 text-sm rounded bg-komal-blue hover:bg-komal-blue-dark text-white font-semibold">Log Bill</button>
                </div>
              </form>
            )}
          </div>
        )}

        {/* Filters - applied server-side (Advances + Bills tables) so this
            scales as vendors/transactions grow */}
        <div className="glass-card p-4 mb-6 flex flex-wrap gap-2 items-center">
          <select value={filters.vendor_id} onChange={(e) => setFilters({ ...filters, vendor_id: e.target.value })} className="border border-gray-300 rounded px-2 py-1.5 text-xs">
            <option value="">All vendors</option>
            {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
          <select value={filters.project_id} onChange={(e) => setFilters({ ...filters, project_id: e.target.value })} className="border border-gray-300 rounded px-2 py-1.5 text-xs">
            <option value="">All projects</option>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} className="border border-gray-300 rounded px-2 py-1.5 text-xs" />
          <span className="text-xs text-gray-400">to</span>
          <input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} className="border border-gray-300 rounded px-2 py-1.5 text-xs" />
          <button onClick={clearFilters} className="text-xs px-3 py-1.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 ml-auto">
            Clear Filters
          </button>
        </div>

        <div className="glass-card">
          <div className="px-5 py-4 border-b border-gray-200">
            <h2 className="font-semibold text-gray-900">Bills ({bills.length})</h2>
          </div>
          {isLoading ? (
            <p className="text-gray-500 text-sm p-5">Loading...</p>
          ) : bills.length === 0 ? (
            <p className="text-gray-500 text-sm p-5">No vendor bills match these filters.</p>
          ) : (
            <ScrollX>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-100">
                  <th className="px-4 py-2 font-medium">Vendor</th>
                  <th className="px-4 py-2 font-medium">Description</th>
                  <th className="px-4 py-2 font-medium">Amount</th>
                  <th className="px-4 py-2 font-medium">Date</th>
                  <th className="px-4 py-2 font-medium">PO #</th>
                  <th className="px-4 py-2 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {bills.map((b) => (
                  <tr key={b.id} className="border-b border-gray-50 last:border-0">
                    <td className="px-4 py-3">
                      {vendorName(b.vendor_id)}
                      <div className="text-xs text-gray-400">{linkLabel(b)}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-500">{b.description || '—'}</td>
                    <td className="px-4 py-3 font-medium">{money(b.amount)}</td>
                    <td className="px-4 py-3">{b.bill_date}</td>
                    <td className="px-4 py-3 text-gray-500">{b.po_number || '—'}</td>
                    <td className="px-4 py-3 space-x-2 whitespace-nowrap">
                      {canLog && (
                        <>
                          <button onClick={() => handleEditBill(b)} className="text-gray-600 hover:underline text-xs font-semibold">Edit</button>
                          <button onClick={() => handleDeleteBill(b)} className="text-red-500 hover:underline text-xs font-semibold">Delete</button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </ScrollX>
          )}
        </div>
      </div>
    </div>
  );
};

export default VendorPaymentsPage;
